const initialStateAttribute = 'data-copilot-review-effort-initial-state'
export const copilotReviewEffortPickerSelector =
  '[data-copilot-review-effort-picker][data-copilot-review-effort-version="2"]'

const coreFieldNames = ['review_effort_level', 'review_effort_level_selected', 'review_effort_client_version'] as const

export const copilotReviewEffortFieldNames = [...coreFieldNames, 'review_effort_source'] as const

const sources = [
  'explicit_selection',
  'previous_review',
  'user_setting',
  'repository_setting',
  'owner_setting',
  'platform_default',
  'enterprise_setting',
] as const

export type CopilotReviewEffortSource = (typeof sources)[number]

type CopilotReviewEffortLevel = 'low' | 'medium' | 'high'

type CopilotReviewEffortResponseCore = {
  review_effort_level: CopilotReviewEffortLevel
  review_effort_level_selected: boolean
  review_effort_client_version: '2'
}

export type CopilotReviewEffortResponse = CopilotReviewEffortResponseCore & {
  review_effort_source?: CopilotReviewEffortSource
}

type CopilotReviewEffortInput = {
  effort: CopilotReviewEffortLevel
  selected: boolean
  source?: CopilotReviewEffortSource
}

type CopilotReviewEffortV2Fields =
  | {
      review_effort_client_version: '2'
      review_effort_level: string
      review_effort_level_selected: boolean
      review_effort_source?: CopilotReviewEffortSource
    }
  | {
      review_effort_client_version: '2'
      review_effort_level?: never
      review_effort_level_selected: false
      review_effort_source?: never
    }

export type CopilotReviewEffortFields =
  | CopilotReviewEffortV2Fields
  | {
      review_effort_level: string
      review_effort_client_version?: never
      review_effort_level_selected?: never
      review_effort_source?: never
    }

export type CopilotReviewEffortRequest = {
  fields: CopilotReviewEffortFields | undefined
  acceptResponse: (response: unknown) => void
  fail: () => void
  dismissFailure?: () => void
}

export class InvalidCopilotReviewEffortError extends Error {
  constructor() {
    super('Unable to read the complete Copilot review effort selection. Please try again.')
    this.name = 'InvalidCopilotReviewEffortError'
  }
}

export type CopilotReviewEffortSubmission = {
  readonly picker: Element
  selectionRevision: number
  responseRevision: number
  serverRevision: number
  context: string
  documentBody: HTMLElement
  fields: CopilotReviewEffortV2Fields
  state: PickerState
}

type PickerState = {
  picker: Element
  selectionRevision: number
  responseRevision: number
  serverRevision: number
  decision?: {effort: CopilotReviewEffortLevel; source?: CopilotReviewEffortSource}
}

const pickerStates = new WeakMap<Element, PickerState>()

function getPickerState(picker: Element) {
  let state = pickerStates.get(picker)
  if (!state || state.picker !== picker) {
    state = {picker, selectionRevision: 0, responseRevision: 0, serverRevision: 0}
    pickerStates.set(picker, state)
  }
  return state
}

function findSupportedPicker(root: Element | null) {
  if (root?.matches(copilotReviewEffortPickerSelector)) return root
  const pickers = root?.querySelectorAll(copilotReviewEffortPickerSelector)
  if (pickers && pickers.length > 1) throw new InvalidCopilotReviewEffortError()
  return pickers?.[0]
}

function readFields(picker: Element) {
  const value = (name: (typeof copilotReviewEffortFieldNames)[number]) => {
    const inputs = picker.querySelectorAll<HTMLInputElement>(`input[name="${name}"]`)
    return inputs.length === 1 && !inputs[0]?.disabled ? inputs[0]?.value : undefined
  }
  const effort = value('review_effort_level')
  const selected = value('review_effort_level_selected')
  return {
    ...(effort === undefined ? {} : {review_effort_level: effort}),
    review_effort_level_selected: selected === 'true' ? true : selected === 'false' ? false : selected,
    review_effort_client_version: value('review_effort_client_version'),
    review_effort_source: value('review_effort_source'),
  }
}

function readDescriptor(picker: Element, attribute: string): unknown {
  if (coreFieldNames.some(name => picker.querySelectorAll(`input[name="${name}"]`).length > 1)) return
  const descriptor = picker.getAttribute(attribute)
  let fields: unknown = readFields(picker)
  if (descriptor !== null) {
    try {
      fields = JSON.parse(descriptor)
    } catch (error) {
      if (error instanceof SyntaxError) return
      throw error
    }
  }
  return fields
}

export function readCopilotReviewEffortResponse(picker: Element, attribute = initialStateAttribute) {
  return parseResponse(readDescriptor(picker, attribute))
}

export function normalizeCopilotReviewEffortSource(value: unknown): CopilotReviewEffortSource | undefined {
  return sources.find(source => source === value)
}

function parseResponse(value: unknown): CopilotReviewEffortResponse | undefined {
  if (!isCopilotReviewEffortResponse(value)) return
  const source = normalizeCopilotReviewEffortSource(
    'review_effort_source' in value ? value.review_effort_source : undefined,
  )
  return {
    review_effort_client_version: value.review_effort_client_version,
    review_effort_level: value.review_effort_level,
    review_effort_level_selected: value.review_effort_level_selected,
    ...(source ? {review_effort_source: source} : {}),
  }
}

function inputFromResponse(response: CopilotReviewEffortResponse): CopilotReviewEffortInput {
  return {
    effort: response.review_effort_level,
    selected: response.review_effort_level_selected,
    source: response.review_effort_source,
  }
}

function fieldsForInput(input: CopilotReviewEffortInput): CopilotReviewEffortV2Fields {
  return {
    review_effort_client_version: '2',
    review_effort_level: input.effort,
    review_effort_level_selected: input.selected,
    ...(input.source ? {review_effort_source: input.source} : {}),
  }
}

export function readCopilotReviewEffortInput(picker: Element): CopilotReviewEffortInput | undefined {
  const response = readCopilotReviewEffortResponse(picker)
  return response ? inputFromResponse(response) : undefined
}

export function collectCopilotReviewEffort(picker: Element | null): CopilotReviewEffortFields | undefined {
  if (!picker) return
  const supported = findSupportedPicker(picker)
  if (!supported) {
    if (
      picker.matches('[data-copilot-review-effort-version], [data-copilot-review-effort-initial-state]') ||
      picker.querySelector(
        '[data-copilot-review-effort-version], [data-copilot-review-effort-initial-state], input[name="review_effort_client_version"], input[name="review_effort_level_selected"], input[name="review_effort_source"]',
      )
    ) {
      throw new InvalidCopilotReviewEffortError()
    }
    const effort = picker.querySelector<HTMLInputElement>('input[name="review_effort_level"]')?.value
    return effort ? {review_effort_level: effort} : undefined
  }
  enhanceCopilotReviewEffortPicker(supported)
  const fields = readFields(supported)
  const response = parseResponse(fields)
  if (!response) throw new InvalidCopilotReviewEffortError()
  return response
}

export function getCopilotReviewEffortContext(picker: Element, form = picker.closest('form')) {
  const location = picker.ownerDocument.location
  return JSON.stringify([
    location.origin,
    location.pathname,
    location.search,
    picker.ownerDocument.head.querySelector('meta[name="user-login"]')?.getAttribute('content'),
    form?.getAttribute('id'),
    form?.getAttribute('action'),
    picker.closest('.js-issue-sidebar-form[data-url]')?.getAttribute('data-url'),
  ])
}

function setField(
  picker: Element,
  name: (typeof copilotReviewEffortFieldNames)[number],
  value: string | boolean,
  container: Element,
) {
  let input = picker.querySelector<HTMLInputElement>(`input[name="${name}"]`)
  for (const duplicate of picker.querySelectorAll(`input[name="${name}"]`)) {
    if (duplicate !== input) duplicate.remove()
  }
  if (!input) {
    input = picker.ownerDocument.createElement('input')
    input.type = 'hidden'
    input.name = name
  }
  if (input.parentElement !== container) container.append(input)
  input.value = String(value)
  input.disabled = false
}

export function enhanceCopilotReviewEffortPicker(picker: Element) {
  if (!picker.matches(copilotReviewEffortPickerSelector) || pickerStates.has(picker)) return
  const input = readCopilotReviewEffortInput(picker)
  if (!input) throw new InvalidCopilotReviewEffortError()
  writeCopilotReviewEffortInput(picker, input)
}

function writeCopilotReviewEffortInput(picker: Element, input: CopilotReviewEffortInput) {
  const fields = fieldsForInput(input)
  const effort = input.effort
  // Primer disables its input when no option is checked.
  const effortContainer = picker.querySelector('action-menu [data-list-inputs="true"]') ?? picker
  for (const name of copilotReviewEffortFieldNames) {
    const value = fields[name]
    if (value === undefined) {
      for (const field of picker.querySelectorAll(`input[name="${name}"]`)) field.remove()
    } else {
      setField(picker, name, value, name === 'review_effort_level' ? effortContainer : picker)
    }
  }
  getPickerState(picker).decision = {effort: input.effort, source: input.source}
  picker.setAttribute('data-copilot-review-effort-version', '2')
  picker.removeAttribute(initialStateAttribute)
  const checked = picker.querySelectorAll('[role="menuitemradio"][aria-checked="true"]')
  const matchesDisplay = checked.length === 1 && checked[0]?.getAttribute('data-value') === effort
  if (!matchesDisplay) {
    for (const item of picker.querySelectorAll('[role="menuitemradio"][data-value]')) {
      item.setAttribute('aria-checked', String(item.getAttribute('data-value') === effort))
    }
    const menu = picker.querySelector('action-menu')
    if (menu) {
      const restoreFocus = menu.contains(picker.ownerDocument.activeElement)
      const replacement = menu.cloneNode(true)
      menu.replaceWith(replacement)
      if (restoreFocus && replacement instanceof Element) {
        replacement.querySelector<HTMLElement>('button[aria-controls]')?.focus()
      }
    }
  }
}

export function preserveCopilotReviewEffortState(
  previous: Element,
  replacement: Element,
  refreshUntouched = false,
): 'incomplete' | 'preserved' | undefined {
  if (
    !previous.matches(copilotReviewEffortPickerSelector) ||
    !replacement.matches('[data-copilot-review-effort-picker]')
  ) {
    return
  }
  const previousState = pickerStates.get(previous)
  if (previousState && previousState.picker !== previous) return
  if (replacement.hasAttribute(initialStateAttribute) && !readCopilotReviewEffortInput(replacement)) {
    return 'incomplete'
  }
  let input = readCopilotReviewEffortInput(previous)
  if (!input) return 'incomplete'

  const state = previousState ?? getPickerState(previous)
  const replacementState = pickerStates.get(replacement)
  if (
    replacementState &&
    replacementState !== state &&
    (replacementState.selectionRevision > 0 ||
      replacementState.responseRevision > 0 ||
      replacementState.serverRevision > 0)
  ) {
    return
  }
  if (refreshUntouched && !input.selected) {
    const fresh = readCopilotReviewEffortInput(replacement)
    if (fresh?.source === 'previous_review') {
      if (input.effort !== fresh.effort || input.source !== fresh.source || input.selected !== fresh.selected) {
        state.serverRevision += 1
      }
      input = fresh
    }
  }
  state.picker = replacement
  pickerStates.set(replacement, state)
  writeCopilotReviewEffortInput(replacement, input)
  return 'preserved'
}

export function recordCopilotReviewEffortSelection(picker: Element, item: HTMLElement) {
  const control = item.matches('[role="menuitemradio"]') ? item : item.querySelector('[role="menuitemradio"]')
  if (!picker.contains(item) || !control || control.matches(':disabled, [aria-disabled="true"]')) {
    return
  }
  const effort = control.getAttribute('data-value')
  if (!isCopilotReviewEffortLevel(effort)) throw new InvalidCopilotReviewEffortError()
  if (readCopilotReviewEffortInput(picker)) enhanceCopilotReviewEffortPicker(picker)
  const state = getPickerState(picker)
  const changed = state.decision?.effort !== effort
  if (changed) state.selectionRevision += 1
  writeCopilotReviewEffortInput(picker, {
    effort,
    selected: changed || readFields(picker).review_effort_level_selected === true,
    source: changed ? 'explicit_selection' : state.decision?.source,
  })
}

export function captureCopilotReviewEffort(picker: Element | null): CopilotReviewEffortSubmission | undefined {
  const root = findSupportedPicker(picker)
  if (!root?.isConnected) return
  const fields = collectCopilotReviewEffort(root)
  if (!fields || fields.review_effort_client_version !== '2') return
  const state = getPickerState(root)
  return {
    get picker() {
      return state.picker
    },
    selectionRevision: state.selectionRevision,
    responseRevision: state.responseRevision,
    serverRevision: state.serverRevision,
    context: getCopilotReviewEffortContext(root),
    documentBody: root.ownerDocument.body,
    fields,
    state,
  }
}

export function isCurrentCopilotReviewEffort(submission: CopilotReviewEffortSubmission) {
  return (
    isRelatedCopilotReviewEffort(submission) &&
    submission.state.serverRevision === submission.serverRevision &&
    submission.state.responseRevision === submission.responseRevision &&
    submission.state.selectionRevision === submission.selectionRevision
  )
}

export function isRelatedCopilotReviewEffort(submission: CopilotReviewEffortSubmission) {
  const {picker, context, documentBody, state} = submission
  return (
    picker.isConnected &&
    picker.matches(copilotReviewEffortPickerSelector) &&
    picker.ownerDocument.body === documentBody &&
    getCopilotReviewEffortContext(picker) === context &&
    pickerStates.get(picker) === state
  )
}

function isCopilotReviewEffortLevel(value: unknown): value is CopilotReviewEffortLevel {
  return value === 'low' || value === 'medium' || value === 'high'
}

export function isCopilotReviewEffortResponse(value: unknown): value is CopilotReviewEffortResponseCore {
  return (
    typeof value === 'object' &&
    value !== null &&
    'review_effort_level' in value &&
    isCopilotReviewEffortLevel(value.review_effort_level) &&
    'review_effort_level_selected' in value &&
    typeof value.review_effort_level_selected === 'boolean' &&
    'review_effort_client_version' in value &&
    value.review_effort_client_version === '2'
  )
}

export function applyCopilotReviewEffortResponse(
  submission: CopilotReviewEffortSubmission | undefined,
  response: unknown,
) {
  if (!submission || !isCurrentCopilotReviewEffort(submission)) return
  const submitted = readCopilotReviewEffortSubmission(response)
  const accepted = readCopilotReviewEffortAcceptance(response)
  if (!accepted && !submitted) return

  const {picker} = submission
  const input = accepted ? inputFromResponse(accepted) : readCopilotReviewEffortInput(picker)
  if (!input) throw new InvalidCopilotReviewEffortError()
  submission.state.responseRevision += 1
  writeCopilotReviewEffortInput(picker, {...input, selected: false})
}

export function readCopilotReviewEffortSubmission(response: unknown) {
  if (typeof response !== 'object' || response === null || !('copilot_review_effort_submitted' in response)) return
  const submitted = parseResponse(response.copilot_review_effort_submitted)
  if (
    !submitted ||
    submitted.review_effort_level_selected ||
    copilotReviewEffortFieldNames.some(name => name in response)
  ) {
    throw new InvalidCopilotReviewEffortError()
  }
  return submitted
}

export function readCopilotReviewEffortAcceptance(response: unknown) {
  const accepted = parseResponse(response)
  if (accepted) return accepted.review_effort_level_selected ? undefined : accepted
  if (typeof response === 'object' && response !== null && coreFieldNames.some(name => name in response)) {
    throw new InvalidCopilotReviewEffortError()
  }
}
